import { ActionTypeEnum, TCommonData, TWAction, TWidget, TWInternalId, TRequestUri, TEmbeddedPlayerInitOptions, TInitPayerOptions } from '@libs/types';
import { addMutableWidgets, updateBottomSheetsWidgetsMap, updateInternalIdWidgets, updateModalWindowsMap } from './features/scenario.slice';
import { fetchJsonAsync, updateScenario } from './features/utils/sharedActions';
import { AppDispatchType } from './hooks';
import { getWidgetByActionWidgetId } from './widgets';

const REQUEST_URI = 'requestUri';

export const setEnvironmentRequestUri = (requestUri?: TRequestUri) => {
    if (requestUri) {
        sessionStorage.setItem(REQUEST_URI, JSON.stringify(requestUri));
    }
};
export const managePs3PlayerCookies = (): Record<string, string> => {
    const prefix = 'data-ps3-player-';
    const cookies: Record<string, string> = {};

    const allCookies = document.cookie.split(';');

    for (const cookie of allCookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(prefix)) {
            const [nameValuePair, ...rest] = trimmedCookie.split('=');
            const cookieName = nameValuePair;
            const cookieValue = rest.join('=');

            const key = cookieName.substring(prefix.length);
            cookies[key] = cookieValue;
        }
    }

    for (const cookieNameWithPrefix in cookies) {
        if (Object.prototype.hasOwnProperty.call(cookies, cookieNameWithPrefix)) {
            document.cookie = `${prefix}${cookieNameWithPrefix}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
    }

    return cookies;
};

const clearSearchParams = () => {
    if (window.history.replaceState) {
        const cleanURL = window.location.protocol + '//' + window.location.host + window.location.pathname;
        window.history.replaceState({ path: cleanURL }, '', cleanURL);
    } else {
        console.warn('History API is not supported in this browser.');
    }
};

export const buildRequestUriFromUrl = (pathname: string, searchParams: URLSearchParams): TRequestUri => {
    const requestUri: TRequestUri = { path: pathname };
    if (searchParams.size !== 0) {
        requestUri.data = Object.fromEntries(searchParams.entries());
    }
    return requestUri;
};

const parseDeeplink = (deeplink?: string): TRequestUri | undefined => {
    if (!deeplink) {
        return undefined;
    }
    try {
        const url = new URL(deeplink, window.location.origin);
        return buildRequestUriFromUrl(url.pathname, new URLSearchParams(url.search));
    } catch {
        console.warn('Invalid deeplink provided, ignoring');
        return undefined;
    }
};

export const getInitPlayerOptions = (deeplink?: string): TInitPayerOptions => {
    const externalData = managePs3PlayerCookies();

    const urlObj = new URL(window.location.href);
    const params = new URLSearchParams(urlObj.search);
    if (params.size !== 0) {
        params.forEach((value, key) => {
            externalData[key] = value as never;
        });
        clearSearchParams();
    }
    const strRequestUri = sessionStorage.getItem(REQUEST_URI) as string;
    const result = {
        externalData,
        requestUri: parseDeeplink(deeplink) ?? (strRequestUri ? (JSON.parse(strRequestUri) as TRequestUri) : undefined),
    } as TInitPayerOptions;
    sessionStorage.removeItem(REQUEST_URI);
    return result;
};

export type TInitParams<TRenderOptions = unknown> = {
    initPayerOptions: TInitPayerOptions;
    renderOptions?: TRenderOptions;
};

export const getInitParams = <TRenderOptions = unknown>(options?: TEmbeddedPlayerInitOptions<TRenderOptions>): TInitParams<TRenderOptions> => {
    const { deeplink, renderOptions, ...passedOptions } = options ?? {};
    const envOptions = getInitPlayerOptions(deeplink);
    return {
        initPayerOptions: {
            ...envOptions,
            ...passedOptions,
            externalData: { ...envOptions.externalData, ...passedOptions.externalData },
        },
        renderOptions,
    };
};

export const getWidgetDataOrUpdatePage = async <TD extends TCommonData>(
    action: TWAction,
    widgetUid: TWidget<TD>['uid'],
    internalId: TWInternalId,
    currentScenarioId: string,
    currentPageId: string,
    dispatch: AppDispatchType,
    isUpdateWidgetByUid = true,
    keepalive?: boolean,
    playerId?: string | undefined,
    isShowLoader?: boolean,
): Promise<TWidget<TD> | undefined> => {
    const json = await dispatch(
        fetchJsonAsync({
            action,
            widgetUid,
            isShowLoader,
            keepalive,
            playerId,
            currentPageId,
            scenarioId: currentScenarioId,
        }),
    ).unwrap();
    const jsonScenario = json?.scenario;
    if (
        internalId &&
        action.type === ActionTypeEnum.Widget &&
        jsonScenario?.scenarioId === currentScenarioId &&
        jsonScenario?.pages?.[0]?.id === currentPageId
    ) {
        const widgets = jsonScenario.pages[0].widgets;
        if (Array.isArray(widgets)) {
            const result = getWidgetByActionWidgetId(widgets, internalId);
            if (result.widget?.uid && isUpdateWidgetByUid) {
                dispatch(addMutableWidgets({ widgetUid, widgets: result.widget }));
            }
            if (result.bottomSheetMap) {
                dispatch(updateBottomSheetsWidgetsMap(result.bottomSheetMap));
            }
            if (result.modalWindowMap) {
                dispatch(updateModalWindowsMap(result.modalWindowMap));
            }
            if (result.internalIdWidgets) {
                dispatch(updateInternalIdWidgets(result.internalIdWidgets));
            }
            if (result.widget) {
                return {
                    ...result.widget,
                    widgets: result.widget?.widgets?.map((widget) => widget?.uid),
                } as TWidget<TD>;
            }
        }
    } else {
        dispatch(updateScenario(json));
    }
    return Promise.resolve(undefined);
};
